import { useState, useEffect, useCallback } from "react";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

/* NexorONE Core UI Widget Component: Proof-of-Reserve (PoR) — council template, verbatim styling.
   Live data binds from GET /api/por-feed (declared audited inventory × live oracle rates,
   cross-attested against chain-9940 state). */
const WIDGET_CSS = `
.bgnzn-por-widget-card {
    width: 100%;
    max-width: 420px;
    background: rgba(11, 15, 25, 0.85);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(176, 183, 189, 0.2);
    border-radius: 10px;
    padding: 24px;
    box-sizing: border-box;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4), 0 0 15px rgba(229, 193, 88, 0.1);
    font-family: 'Helvetica', 'Arial', sans-serif;
}
.widget-header-matrix {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    border-bottom: 1px solid rgba(176, 183, 189, 0.15);
    padding-bottom: 16px;
}
.bgnzn-vector-crest { width: 45px; height: 45px; }
.widget-main-title {
    color: #e5c158;
    margin: 0;
    font-size: 1.05rem;
    letter-spacing: 2px;
    font-weight: 800;
}
.widget-sub-status {
    color: #5bc6e9;
    margin: 4px 0 0 0;
    font-size: 0.75rem;
    letter-spacing: 1px;
}
.widget-sub-status.por-offline { color: #ff6b76; }
.por-asset-matrix-display { display: flex; flex-direction: column; gap: 12px; }
.asset-data-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(11, 15, 25, 0.6);
    padding: 12px 16px;
    border-radius: 6px;
    border-left: 3px solid transparent;
}
.border-gold { border-left-color: #e5c158; }
.border-steel { border-left-color: #b0b7bd; }
.border-green { border-left-color: #4caf50; }
.asset-ticker-lbl { color: #b0b7bd; font-size: 0.85rem; font-weight: 500; min-width: 0; flex-shrink: 1; }
.asset-value-wrapper { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.live-weight-metric { color: #ffffff; font-size: 0.95rem; font-weight: 700; white-space: nowrap; }
.fiat-valuation-equivalent { color: rgba(255, 255, 255, 0.5); font-size: 0.75rem; margin-top: 2px; }
.por-audit-footer {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
}
.cryptographic-hash-seal {
    font-size: 0.65rem;
    color: rgba(176, 183, 189, 0.4);
    font-family: 'Courier New', monospace;
    text-align: center;
}
.verify-signature-btn {
    width: 100%;
    padding: 10px;
    background: none;
    border: 1px solid #e5c158;
    color: #e5c158;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: all 0.2s ease;
}
.verify-signature-btn:hover {
    background: rgba(229, 193, 88, 0.1);
    box-shadow: 0 0 10px rgba(229, 193, 88, 0.2);
}
.verify-signature-btn:disabled { opacity: 0.55; cursor: wait; }
.por-aggregate-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 16px 0;
    margin-top: 4px;
    border-top: 1px dashed rgba(176, 183, 189, 0.2);
    font-size: 0.8rem;
    color: #b0b7bd;
}
.por-aggregate-row strong { color: #e5c158; }
`;

const fmtWeight = (n, unit) =>
  `${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${unit}`;
const fmtUsd = (n) =>
  "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 });

const PorWidget = () => {
  const [feed, setFeed] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/por-feed`);
      if (res.ok) setFeed(await res.json());
    } catch {
      /* keep last good feed */
    }
    setSyncing(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const online = feed?.node?.status === "ONLINE";
  const rootHash = feed?.por_root_hash || "";
  const shortHash = rootHash ? `${rootHash.slice(0, 8)}...${rootHash.slice(-5)}` : "sealing…";

  return (
    <div className="bgnzn-por-widget-card" data-testid="por-widget">
      <style>{WIDGET_CSS}</style>
      <div className="widget-header-matrix">
        <div className="brand-crest-frame">
          <svg className="bgnzn-vector-crest" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#e5c158" strokeWidth="2" />
            <path d="M35,70 C40,55 45,40 50,25 C55,40 60,55 65,70 Z" fill="#e5c158" />
            <path d="M25,70 Q50,60 75,70" stroke="#b0b7bd" strokeWidth="3" fill="none" />
          </svg>
        </div>
        <div className="header-titles">
          <h3 className="widget-main-title">{feed?.title || "BMEX REPOSITORY AUDIT"}</h3>
          <p
            className={`widget-sub-status${online ? "" : " por-offline"}`}
            data-testid="por-node-status"
          >
            ● GENESIS NODE 9940 {online ? "ONLINE" : "OFFLINE"}
            {online && feed?.node?.block_height ? ` · BLOCK #${feed.node.block_height}` : ""}
          </p>
        </div>
      </div>

      <div className="por-asset-matrix-display" data-testid="por-asset-matrix">
        {(feed?.assets || []).map((a) => (
          <div
            key={a.ticker}
            className={`asset-data-row border-${a.border}`}
            data-testid={`por-row-${a.ticker}`}
          >
            <span className="asset-ticker-lbl">
              {a.ticker} ({a.label})
            </span>
            <div className="asset-value-wrapper">
              <span className="live-weight-metric" data-testid={`por-weight-${a.ticker}`}>
                {fmtWeight(a.weight, a.unit)}
              </span>
              <span className="fiat-valuation-equivalent" data-testid={`por-usd-${a.ticker}`}>
                {fmtUsd(a.valuation_usd)}
              </span>
            </div>
          </div>
        ))}
        {!feed && <div className="asset-data-row border-steel">Synchronizing oracle telemetry…</div>}
      </div>

      {feed && (
        <div className="por-aggregate-row" data-testid="por-aggregate">
          <span>Aggregate audited valuation</span>
          <strong>{fmtUsd(feed.aggregate_valuation_usd)}</strong>
        </div>
      )}

      <footer className="por-audit-footer">
        <span className="cryptographic-hash-seal" data-testid="por-root-hash">
          PoR Ledger Root Hash: {shortHash}
          {online && feed?.node?.clearing_engine_vault_gAu != null && (
            <>
              <br />
              chain attestation:{" "}
              {Number(feed.node.clearing_engine_vault_gAu).toLocaleString("en-US")} gAu engine vault
            </>
          )}
        </span>
        <button
          className="verify-signature-btn"
          data-testid="por-sync-button"
          onClick={refresh}
          disabled={syncing}
        >
          {syncing ? "Syncing…" : "Sync Live Oracles"}
        </button>
      </footer>
    </div>
  );
};

export default PorWidget;
